import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Chip,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Assignment as AssignIcon,
  CheckCircle as ResolveIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  PriorityHigh as PriorityIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

import Page from "./Page";
import useApi from "../../hooks/useApi";

const SupportTickets = () => {
  const { getData, postData } = useApi();
  const userId = useSelector((state) => state.authentication.userId);

  const [activeTab, setActiveTab] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newCategory, setNewCategory] = useState("other");
  const [newPriority, setNewPriority] = useState("medium");
  const [newAttachments, setNewAttachments] = useState([]);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [ticketCounts, setTicketCounts] = useState({
    new: 0,
    assigned: 0,
    in_progress: 0,
    resolved: 0,
  });

  useEffect(() => {
    loadData();
    loadUsers();
    loadTicketCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadTicketCounts = async () => {
    try {
      const statuses = ["new", "assigned", "in_progress", "resolved"];
      const counts = {
        new: 0,
        assigned: 0,
        in_progress: 0,
        resolved: 0,
      };

      for (const status of statuses) {
        const response = await postData({
          entityName: "SupportTicket",
          action: "getAllTickets",
          data: { status },
        });
        if (response.isSuccessful) {
          counts[status] = response.data?.length || 0;
        }
      }

      setTicketCounts(counts);
    } catch (error) {
      console.error("Error loading ticket counts:", error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      let status = null;
      if (activeTab === 0) status = "new";
      if (activeTab === 1) status = "assigned";
      if (activeTab === 2) status = "in_progress";
      if (activeTab === 3) status = "resolved";

      const response = await postData({
        entityName: "SupportTicket",
        action: "getAllTickets",
        data: { status },
      });
      if (response.isSuccessful) {
        setTickets(response.data || []);
      } else {
        setTickets([]);
      }
    } catch (error) {
      console.error("Error loading tickets:", error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await getData({
        entityName: "User",
        action: "get",
      });
      if (response.isSuccessful) {
        const supportUsers = (response.data || []).filter(
          (u) =>
            u.permissions?.canAccessSupport ||
            ["admin", "sysadmin", "systemAdmin"].includes(u.role),
        );
        setUsers(supportUsers);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "critical":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "new":
        return "error";
      case "assigned":
        return "warning";
      case "in_progress":
        return "info";
      case "resolved":
        return "success";
      default:
        return "default";
    }
  };

  const handleViewTicket = async (ticket) => {
    try {
      const response = await postData({
        entityName: "SupportTicket",
        action: "getTicketById",
        data: { ticketId: ticket.id },
      });
      if (response.isSuccessful) {
        setSelectedTicket(response.data);
        setDialogOpen(true);
      }
    } catch (error) {
      console.error("Error loading ticket:", error);
    }
  };

  const handleAssignTicket = (ticket) => {
    setSelectedTicket(ticket);
    setSelectedUser(ticket.assignedTo || "");
    setAssignDialogOpen(true);
  };

  const handleDeleteTicket = (ticket) => {
    setTicketToDelete(ticket);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!ticketToDelete) return;

    try {
      const response = await postData({
        entityName: "SupportTicket",
        action: "delete",
        data: {
          id: ticketToDelete.id,
        },
      });

      if (response.isSuccessful) {
        setDeleteConfirmOpen(false);
        setTicketToDelete(null);
        if (dialogOpen && selectedTicket?.id === ticketToDelete.id) {
          setDialogOpen(false);
        }
        loadData();
      }
    } catch (error) {
      console.error("Error deleting ticket:", error);
    }
  };

  const submitAssignment = async () => {
    if (!selectedUser || !selectedTicket) return;

    try {
      const response = await postData({
        entityName: "SupportTicket",
        action: "assignTicket",
        data: {
          ticketId: selectedTicket.id,
          assignedTo: selectedUser,
        },
      });

      if (response.isSuccessful) {
        setAssignDialogOpen(false);
        setSelectedUser("");
        loadData();
      }
    } catch (error) {
      console.error("Error assigning:", error);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket || !userId) return;

    try {
      const response = await postData({
        entityName: "SupportTicket",
        action: "addMessage",
        data: {
          ticketId: selectedTicket.id,
          userId,
          message: replyMessage,
        },
      });

      if (response.isSuccessful) {
        setReplyMessage("");
        // Reload ticket details
        const updatedTicket = await postData({
          entityName: "SupportTicket",
          action: "getTicketById",
          data: { ticketId: selectedTicket.id },
        });
        if (updatedTicket.isSuccessful) {
          setSelectedTicket(updatedTicket.data);
        }
      }
    } catch (error) {
      console.error("Error sending reply:", error);
    }
  };

  const handleMarkInProgress = async () => {
    if (!selectedTicket) return;

    try {
      const response = await postData({
        entityName: "SupportTicket",
        action: "markInProgress",
        data: {
          ticketId: selectedTicket.id,
        },
      });

      if (response.isSuccessful) {
        loadData();
        loadTicketCounts();
      }
    } catch (error) {
      console.error("Error marking ticket in progress:", error);
    }
  };

  const handleResolveTicket = async () => {
    if (!selectedTicket) return;

    try {
      const response = await postData({
        entityName: "SupportTicket",
        action: "resolveTicket",
        data: {
          ticketId: selectedTicket.id,
          resolutionNote: resolutionNote.trim() || "Resolved by admin",
        },
      });

      if (response.isSuccessful) {
        setDialogOpen(false);
        setResolutionNote("");
        loadData();
      }
    } catch (error) {
      console.error("Error resolving ticket:", error);
    }
  };

  const handleCreateTicket = async () => {
    if (!userId || !newTitle.trim() || !newDescription.trim()) return;

    try {
      const attachmentsPayload = await Promise.all(
        newAttachments.map(async (file) => ({
          filename: file.name,
          base64: await fileToBase64(file),
        })),
      );

      const response = await postData({
        entityName: "SupportTicket",
        action: "add",
        data: {
          createdBy: userId,
          title: newTitle.trim(),
          description: newDescription.trim(),
          category: newCategory,
          priority: newPriority,
          attachments: attachmentsPayload,
        },
      });

      if (response.isSuccessful) {
        setCreateDialogOpen(false);
        setNewTitle("");
        setNewDescription("");
        setNewCategory("other");
        setNewPriority("medium");
        setNewAttachments([]);
        loadData();
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result?.toString() || "");
      reader.onerror = (error) => reject(error);
    });

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString();
  };

  return (
    <Page pageName="support-tickets">
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
          >
            Support Tickets
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={() => setCreateDialogOpen(true)}
              disabled={!userId}
            >
              New Ticket
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadData}
              variant="outlined"
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  New
                  <Chip
                    label={ticketCounts.new}
                    size="small"
                    color={activeTab === 0 ? "primary" : "default"}
                    variant={activeTab === 0 ? "filled" : "outlined"}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  Assigned
                  <Chip
                    label={ticketCounts.assigned}
                    size="small"
                    color={activeTab === 1 ? "primary" : "default"}
                    variant={activeTab === 1 ? "filled" : "outlined"}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  In Progress
                  <Chip
                    label={ticketCounts.in_progress}
                    size="small"
                    color={activeTab === 2 ? "primary" : "default"}
                    variant={activeTab === 2 ? "filled" : "outlined"}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  Resolved
                  <Chip
                    label={ticketCounts.resolved}
                    size="small"
                    color={activeTab === 3 ? "primary" : "default"}
                    variant={activeTab === 3 ? "filled" : "outlined"}
                  />
                </Box>
              }
            />
          </Tabs>
        </Paper>

        {/* Tickets Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ticket #</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow
                  key={ticket.id}
                  sx={{
                    bgcolor:
                      ticket.priority === "critical"
                        ? "#ffebee"
                        : ticket.priority === "high"
                          ? "#fff3e0"
                          : "inherit",
                  }}
                >
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                    >
                      {ticket.ticketNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={
                        ticket.priority === "critical" ? (
                          <PriorityIcon fontSize="small" />
                        ) : null
                      }
                      label={ticket.priority}
                      color={getPriorityColor(ticket.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: "250px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {ticket.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{ticket.category}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {ticket.createdByName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {ticket.assignedToName || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(ticket.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.status}
                      size="small"
                      color={getStatusColor(ticket.status)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleViewTicket(ticket)}
                      title="View Details"
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    {ticket.status !== "resolved" && (
                      <IconButton
                        size="small"
                        onClick={() => handleAssignTicket(ticket)}
                        title="Assign"
                      >
                        <AssignIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTicket(ticket)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {tickets.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    align="center"
                  >
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ py: 4 }}
                    >
                      No tickets found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create Ticket Dialog */}
        <Dialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create Support Ticket</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <TextField
              label="Title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              required
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newCategory}
                label="Category"
                onChange={(e) => setNewCategory(e.target.value)}
              >
                <MenuItem value="login_issue">Login issue</MenuItem>
                <MenuItem value="sync_problem">Sync problem</MenuItem>
                <MenuItem value="survey_question">Survey question</MenuItem>
                <MenuItem value="app_crash">App crash</MenuItem>
                <MenuItem value="data_issue">Data issue</MenuItem>
                <MenuItem value="feature_request">Feature request</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={newPriority}
                label="Priority"
                onChange={(e) => setNewPriority(e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              component="label"
            >
              Add Images
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) =>
                  setNewAttachments(Array.from(e.target.files || []))
                }
              />
            </Button>
            {newAttachments.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {newAttachments.map((file, idx) => (
                  <Typography
                    key={`${file.name}-${idx}`}
                    variant="caption"
                  >
                    {file.name}
                  </Typography>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateTicket}
              variant="contained"
              disabled={!userId || !newTitle.trim() || !newDescription.trim()}
            >
              Create Ticket
            </Button>
          </DialogActions>
        </Dialog>

        {/* Ticket Details Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Ticket Details - {selectedTicket?.ticketNumber}
          </DialogTitle>
          <DialogContent>
            {selectedTicket && (
              <Box>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                  >
                    Title
                  </Typography>
                  <Typography
                    variant="body1"
                    gutterBottom
                  >
                    {selectedTicket.title}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                  >
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    gutterBottom
                  >
                    {selectedTicket.description}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                    >
                      Priority
                    </Typography>
                    <Chip
                      label={selectedTicket.priority}
                      color={getPriorityColor(selectedTicket.priority)}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                    >
                      Status
                    </Typography>
                    <Chip
                      label={selectedTicket.status}
                      color={getStatusColor(selectedTicket.status)}
                      size="small"
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                    >
                      Category
                    </Typography>
                    <Typography variant="body2">
                      {selectedTicket.category}
                    </Typography>
                  </Box>
                </Box>

                {selectedTicket.attachments?.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Attachments
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}
                    >
                      {selectedTicket.attachments.map((att, idx) => (
                        <Box
                          key={`${att.filename || "file"}-${idx}`}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {att.url?.startsWith("data:image") ? (
                            <>
                              <Box
                                component="img"
                                src={att.url}
                                alt={att.filename || "Attachment"}
                                onClick={() =>
                                  setSelectedImage({
                                    url: att.url,
                                    filename: att.filename,
                                  })
                                }
                                sx={{
                                  maxWidth: "200px",
                                  maxHeight: "200px",
                                  borderRadius: 1,
                                  border: "1px solid #ccc",
                                  cursor: "pointer",
                                  transition: "transform 0.2s",
                                  "&:hover": {
                                    transform: "scale(1.05)",
                                  },
                                }}
                              />
                              <Typography variant="caption">
                                {att.filename || "Image"}
                              </Typography>
                            </>
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              href={att.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {att.filename || "Attachment"}
                            </Button>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Messages */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                  >
                    Conversation
                  </Typography>
                  {selectedTicket.messages?.map((msg, index) => {
                    const isRoleAdminMessage = msg.role === "admin";

                    return (
                      <Paper
                        key={index}
                        sx={{
                          p: 2,
                          mb: 1,
                          bgcolor: isRoleAdminMessage ? "#e3f2fd" : "#f5f5f5",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight="bold"
                          >
                            {msg.userName}
                            {isRoleAdminMessage && " (Admin)"}
                          </Typography>
                          <Typography variant="caption">
                            {formatDate(msg.createdAt)}
                          </Typography>
                        </Box>
                        <Typography variant="body2">{msg.message}</Typography>
                      </Paper>
                    );
                  })}
                </Box>

                {/* Reply Section */}
                {selectedTicket.status !== "resolved" && (
                  <Box sx={{ mb: 3 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Reply to ticket"
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                    />
                    <Button
                      startIcon={<SendIcon />}
                      onClick={handleSendReply}
                      variant="contained"
                      sx={{ mt: 1 }}
                      disabled={!replyMessage.trim()}
                    >
                      Send Reply
                    </Button>
                  </Box>
                )}

                {/* Mark In Progress */}
                {selectedTicket.status === "assigned" && (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      onClick={handleMarkInProgress}
                      variant="outlined"
                      color="info"
                      fullWidth
                    >
                      Mark as In Progress
                    </Button>
                  </Box>
                )}

                {/* Resolution Section */}
                {selectedTicket.status !== "resolved" && (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Resolution notes (optional)"
                      value={resolutionNote}
                      onChange={(e) => setResolutionNote(e.target.value)}
                      placeholder="Enter resolution details..."
                    />
                    <Button
                      startIcon={<ResolveIcon />}
                      onClick={handleResolveTicket}
                      variant="contained"
                      color="success"
                      sx={{ mt: 1 }}
                    >
                      Resolve Ticket
                    </Button>
                  </Box>
                )}

                {selectedTicket.resolution && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: "#e8f5e9" }}>
                    <Typography
                      variant="subtitle2"
                      color="success.main"
                      gutterBottom
                    >
                      Resolution
                    </Typography>
                    <Typography variant="body2">
                      {selectedTicket.resolution}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Assign Dialog */}
        <Dialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
        >
          <DialogTitle>Assign Ticket</DialogTitle>
          <DialogContent>
            <FormControl
              fullWidth
              sx={{ mt: 2 }}
            >
              <InputLabel>Assign To</InputLabel>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                label="Assign To"
              >
                {users.map((user) => (
                  <MenuItem
                    key={user.id}
                    value={user.id}
                  >
                    {user.legalName || user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={submitAssignment}
              variant="contained"
              disabled={!selectedUser}
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>

        {/* Image Viewer Modal */}
        <Dialog
          open={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          maxWidth="lg"
        >
          <DialogTitle>
            {selectedImage?.filename || "Image"}
            <IconButton
              onClick={() => setSelectedImage(null)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "grey.500",
              }}
            >
              ✕
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
            {selectedImage?.url && (
              <Box
                component="img"
                src={selectedImage.url}
                alt={selectedImage.filename || "Image"}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  objectFit: "contain",
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
        >
          <DialogTitle>Delete Ticket?</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete ticket{" "}
              <strong>#{ticketToDelete?.ticketNumber}</strong>? This action
              cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Page>
  );
};

export default SupportTickets;
